/**
 * API Client for Jonkai Publisher
 * Handles all API requests with authentication
 */

import type {
  APIResponse,
  PaginatedResponse,
  Brand,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GenerateCaptionRequest,
  GenerateCaptionResponse,
  CalendarEvent,
  Template,
  SocialPlatform,
  PostType,
} from '../types';

const API_BASE_URL = (import.meta as any).env?.DEV ? 'http://localhost:8788/api' : '/api';

class APIClient {
  private token: string | null = null;

  constructor() {
    // Load token from localStorage
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Request failed',
      })) as { error?: string };
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // ==================== Authentication ====================

  async login(email: string, password: string) {
    const response = await this.request<APIResponse<{ token: string; user: any }>>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }
    );

    if (response.success && response.data?.token) {
      this.setToken(response.data.token);
    }

    return response;
  }

  async logout() {
    try {
      await this.request<APIResponse>('/auth/logout', { method: 'POST' });
    } finally {
      this.clearToken();
    }
  }

  async getCurrentUser() {
    return this.request<APIResponse>('/auth/me');
  }

  async register(email: string, password: string, name?: string) {
    const response = await this.request<APIResponse<{ token: string; user: any }>>(
      '/auth/register',
      {
        method: 'POST',
        body: JSON.stringify({ email, password, name }),
      }
    );

    if (response.success && response.data?.token) {
      this.setToken(response.data.token);
    }

    return response;
  }

  async forgotPassword(email: string) {
    return this.request<APIResponse<{ newPassword?: string; message: string }>>(
      '/auth/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async verifyEmail(token: string) {
    return this.request<APIResponse>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification() {
    return this.request<APIResponse<{ verification_token?: string }>>('/auth/resend-verification', {
      method: 'POST',
    });
  }

  // ==================== Brands ====================

  async getBrands() {
    return this.request<APIResponse<Brand[]>>('/brands');
  }

  async getBrand(id: string) {
    return this.request<APIResponse<Brand>>(`/brands/${id}`);
  }

  // ==================== Posts ====================

  async getPosts(params?: {
    brand_id?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.brand_id) queryParams.set('brand_id', params.brand_id);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());

    const query = queryParams.toString();
    return this.request<PaginatedResponse<Post>>(
      `/posts${query ? `?${query}` : ''}`
    );
  }

  async getPost(id: string) {
    return this.request<APIResponse<Post>>(`/posts/${id}`);
  }

  async createPost(data: CreatePostRequest) {
    return this.request<APIResponse<{ id: string }>>('/posts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePost(id: string, data: UpdatePostRequest) {
    return this.request<APIResponse>(`/posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deletePost(id: string) {
    return this.request<APIResponse>(`/posts/${id}`, {
      method: 'DELETE',
    });
  }

  async retryPost(id: string) {
    return this.request<APIResponse>(`/posts/${id}/retry`, {
      method: 'POST',
    });
  }

  async bulkUpdatePosts(updates: Array<{ id: string; scheduled_at: number }>) {
    return this.request<APIResponse>('/posts/bulk-update', {
      method: 'POST',
      body: JSON.stringify({ updates }),
    });
  }

  // ==================== Content Generation (J2K2) ====================

  async generateCaption(data: GenerateCaptionRequest) {
    return this.request<APIResponse<GenerateCaptionResponse>>('/generate/caption', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async generateHashtags(data: {
    brand_id: string;
    content: string;
    platform: string;
    count?: number;
  }) {
    return this.request<APIResponse<{ hashtags: string[] }>>('/generate/hashtags', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBestPostingTimes(data: {
    brand_id: string;
    platforms: string[];
    date_range?: { start: number; end: number };
  }) {
    return this.request<APIResponse<{
      suggestions: Array<{
        platform: string;
        time: number;
        reason: string;
        confidence: number;
      }>;
      analysis: string;
    }>>('/generate/best-times', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== Media Upload ====================

  async uploadMedia(file: File, brandId?: string) {
    const formData = new FormData();
    formData.append('file', file);
    if (brandId) formData.append('brand_id', brandId);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE_URL}/media/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'Upload failed',
      })) as { error?: string };
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json() as Promise<APIResponse<{
      id: string;
      url: string;
      filename: string;
      size: number;
      type: string;
    }>>;
  }

  async deleteMedia(id: string) {
    return this.request<APIResponse>(`/media/${id}`, {
      method: 'DELETE',
    });
  }

  async listMedia(params?: { brand_id?: string; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.brand_id) queryParams.set('brand_id', params.brand_id);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    const query = queryParams.toString();
    return this.request<APIResponse<Array<{
      id: string;
      url: string;
      filename: string;
      size: number;
      type: string;
      created_at: number;
    }>>>(`/media${query ? `?${query}` : ''}`);
  }

  // ==================== Analytics ====================

  async getPostAnalytics(postId: string) {
    return this.request<APIResponse<Array<{
      id: string;
      post_id: string;
      platform: string;
      likes: number;
      comments: number;
      shares: number;
      views: number;
      reach: number;
      engagement_rate: number;
      synced_at: number | null;
      created_at: number;
      updated_at: number;
    }>>>(`/analytics/${postId}`);
  }

  async syncPostAnalytics(postId: string) {
    return this.request<APIResponse<{
      synced_platforms: string[];
      synced_at: number;
    }>>(`/analytics/sync/${postId}`, {
      method: 'POST',
    });
  }

  async getPerformanceInsights(postId: string) {
    return this.request<APIResponse<{
      overall_performance: string;
      platform_analysis: Array<{
        platform: string;
        performance: 'excellent' | 'good' | 'average' | 'poor';
        insights: string;
      }>;
      recommendations: string[];
      best_performing_platform: string;
      improvement_areas: string[];
    }>>(`/analytics/insights/${postId}`, {
      method: 'POST',
    });
  }

  // ==================== Templates ====================

  async getTemplates(brandId?: string) {
    const queryParams = new URLSearchParams();
    if (brandId) queryParams.set('brand_id', brandId);

    const query = queryParams.toString();
    return this.request<APIResponse<Template[]>>(
      `/templates${query ? `?${query}` : ''}`
    );
  }

  async createTemplate(data: {
    brand_id: string;
    name: string;
    description?: string;
    content_template: string;
    platforms: SocialPlatform[];
    post_type?: PostType;
    hashtags?: string[];
    is_public?: boolean;
  }) {
    return this.request<APIResponse<{ id: string }>>('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async useTemplate(templateId: string) {
    return this.request<APIResponse>(`/templates/${templateId}/use`, {
      method: 'POST',
    });
  }

  async deleteTemplate(templateId: string) {
    return this.request<APIResponse>(`/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // ==================== Calendar ====================

  async getCalendarEvents(params?: {
    start?: number;
    end?: number;
    brand_id?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.start) queryParams.set('start', params.start.toString());
    if (params?.end) queryParams.set('end', params.end.toString());
    if (params?.brand_id) queryParams.set('brand_id', params.brand_id);

    const query = queryParams.toString();
    return this.request<APIResponse<CalendarEvent[]>>(
      `/calendar${query ? `?${query}` : ''}`
    );
  }

  // ==================== Brand DNA (Holo.ai-style) ====================

  async getBrandDNA(brandId: string) {
    return this.request<APIResponse<{
      id: string;
      brand_id: string;
      website_url: string | null;
      website_scraped_at: number | null;
      guidelines_md: string | null;
      tone: string[];
      personality_traits: string[];
      writing_style: {
        sentence_length: string;
        formality: string;
        vocabulary: string;
      };
      common_phrases: string[];
      hashtag_style: string[];
      emoji_usage: {
        frequency: string;
        types: string[];
      };
      cta_patterns: string[];
      target_audience: {
        demographics: string[];
        interests: string[];
        pain_points: string[];
      };
      industry_keywords: string[];
      competitor_differentiation: string | null;
      preferred_content_types: string[];
      posting_themes: string[];
      analysis_confidence: number;
    } | null>>(`/brand-dna/${brandId}`);
  }

  async analyzeBrandWebsite(brandId: string, websiteUrl: string) {
    return this.request<APIResponse<{
      id: string;
      brand_id: string;
      tone: string[];
      personality_traits: string[];
      writing_style: any;
      analysis_confidence: number;
      created?: boolean;
      updated?: boolean;
    }>>('/brand-dna/analyze', {
      method: 'POST',
      body: JSON.stringify({ brand_id: brandId, website_url: websiteUrl }),
    });
  }

  // ==================== Content Pool (Bulk Generation) ====================

  async getContentPool(params?: {
    brand_id?: string;
    status?: 'pending' | 'approved' | 'rejected' | 'edited' | 'scheduled';
    batch_id?: string;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.brand_id) queryParams.set('brand_id', params.brand_id);
    if (params?.status) queryParams.set('status', params.status);
    if (params?.batch_id) queryParams.set('batch_id', params.batch_id);
    if (params?.limit) queryParams.set('limit', params.limit.toString());

    const query = queryParams.toString();
    return this.request<APIResponse<Array<{
      id: string;
      brand_id: string;
      title: string | null;
      content: string;
      platforms: SocialPlatform[];
      hashtags: string[];
      content_type: string;
      generation_batch_id: string | null;
      status: string;
      suggested_post_time: number | null;
      quality_score: number;
      engagement_prediction: number;
      tags: string[];
      created_at: number;
    }>>>(`/content-pool${query ? `?${query}` : ''}`);
  }

  async generateBulkContent(params: {
    brand_id: string;
    content_count: number;
    platforms: SocialPlatform[];
    themes?: string[];
    use_brand_dna?: boolean;
  }) {
    return this.request<APIResponse<{
      batch_id: string;
      items_generated: number;
      content_pool_ids: string[];
    }>>('/content-pool/generate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async updateContentPoolItem(contentId: string, updates: {
    status?: 'pending' | 'approved' | 'rejected' | 'edited' | 'scheduled';
    content?: string;
    title?: string;
    rejection_reason?: string;
    platforms?: SocialPlatform[];
    hashtags?: string[];
    suggested_post_time?: number;
  }) {
    return this.request<APIResponse>(`/content-pool/${contentId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async scheduleFromContentPool(contentId: string, scheduledAt?: number) {
    return this.request<APIResponse<{
      post_id: string;
      content_pool_id: string;
    }>>(`/content-pool/${contentId}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    });
  }

  async deleteContentPoolItem(contentId: string) {
    return this.request<APIResponse>(`/content-pool/${contentId}`, {
      method: 'DELETE',
    });
  }
}

export const apiClient = new APIClient();
